import { useEffect, useState } from "react";
import { Form, Input, Button, Select, message } from "antd";
import "antd/dist/antd.css";
import { postRequest, getRequest } from "../../../http/axiosConfig/config";
import urls from "../../../http/endpoints/urls";


const { TextArea } = Input;
const { Option } = Select;

const FeedbackPage = () => {
  const [form] = Form.useForm();
  const [step, setStep] = useState(null); // "new" or "previous"
  const [datesList, setDatesList] = useState([]);
  const [unionResponse, setUnionResponse] = useState("");
  const [isViewMode, setIsViewMode] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  let customerId = localStorage.getItem("userId");

  const maxChars = 300;

  // Load previous feedback dates
  useEffect(() => {
    const loadDates = async () => {
      const res = await getRequest(urls.feedback_get(customerId));
      setDatesList(res);
    };
    loadDates();
  }, []);

  // Fetch previous feedback
  const fetchFeedbackById = async (feedbackId) => {
    try {
      const res = await getRequest(urls.feedback_get_by_id(feedbackId));

      if (res.success) {
        form.setFieldsValue({
          subject: res.data.subject,
          feedback: res.data.feedback
        });

        setUnionResponse(res.data.replier_remarks);
        setCharCount(res.data.feedback?.length || 0);
        setIsViewMode(true);
      } else {
        message.error(res.message || "Failed to fetch feedback");
      }
    } catch (error) {
      message.error("Error fetching feedback");
    }
  };

  const handleSelectChange = (value) => {
    setSelectedDate(value);

    if (value) {
      fetchFeedbackById(value);
    } else {
      handleClear();
    }
  };

  const handleSubmit = async (values) => {
    const newRecord = { feedback: values.feedback, subject: values.subject, customer_code: customerId }
    try {
      const res = await postRequest(urls.feedback_post(), newRecord);
      if (res.success) {
        message.success(res?.message || "Feedback submitted successfully!");
      } else {
        message.error(res?.message || "Failed to submit feedback");
      }
    } catch (error) {
      message.error(error?.message || "Failed to submit feedback");
    }

    handleClear();
  };

  const handleClear = () => {
    form.resetFields();
    setUnionResponse("");
    setIsViewMode(false);
    setCharCount(0);
    setSelectedDate(null);
  };

  // 🔹 Render intermediate UI (New / Previous)
  if (!step) {
    return (
      <div className="feedback-container">
        <div className="feedback-card" style={{ textAlign: "center" }}>
          <h2>Feedback</h2>
          <Button
            type="primary"
            style={{ margin: "10px 0", width: "200px" }}
            onClick={() => setStep("new")}
          >
            New Feedback
          </Button>
          <br />
          <Button
            style={{ margin: "10px 0", width: "200px" }}
            onClick={() => setStep("previous")}
          >
            Previous Feedback
          </Button>
        </div>
      </div>
    );
  }

  // 🔹 Render feedback form
  return (
    <div className="feedback-container">
      <div className="feedback-card" style={{ height: '60vh' ,overflowY: 'auto'}}>
        <h2>Feedback</h2>
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          {/* Dropdown only for previous feedback */}
          {step === "previous" && (
            <Form.Item label="Select Previous Feedback">
              <Select
                placeholder="Select previous feedback"
                value={selectedDate}
                onChange={handleSelectChange}
                allowClear
              >
                {datesList?.map((item) => (
                  <Option key={item.feedback_id} value={item.feedback_id}>
                    {item.submitted_on}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}

          {/* Fields shown for New Feedback OR after a Previous Feedback is selected */}
          {(step === "new" || isViewMode) && (
            <>
              {/* Subject */}
              <Form.Item
                label="Subject"
                name="subject"
                rules={[{ required: true }]}
              >
                {isViewMode ? (
                  <div
                    style={{
                      padding: "8px 12px",
                      background: "#f5f5f5",
                      borderRadius: "6px",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    {form.getFieldValue("subject")}
                  </div>
                ) : (
                  <Input />
                )}
              </Form.Item>

              {/* Feedback */}
              <Form.Item
                label="Your Feedback"
                name="feedback"
                rules={[{ required: true }]}
              >
                {isViewMode ? (
                  <div
                    style={{
                      padding: "8px 12px",
                      background: "#f5f5f5",
                      borderRadius: "6px",
                      minHeight: "100px",
                      whiteSpace: "pre-wrap",
                      color: "#333",
                      fontSize: "14px",
                      lineHeight: "1.6",
                    }}
                  >
                    {form.getFieldValue("feedback")}
                  </div>
                ) : (
                  <TextArea
                    rows={5}
                    maxLength={maxChars}
                    onChange={(e) => setCharCount(e.target.value.length)}
                  />
                )}
              </Form.Item>

              {/* Character Count (New only) */}
              {!isViewMode && (
                <div style={{ textAlign: "right", marginBottom: 10, marginTop: -20 }}>
                  {maxChars - charCount} characters remaining
                </div>
              )}

              {/* Response Section (View mode only) */}
              {isViewMode && (
                <Form.Item label="Response from Marketing Section">
                  <div
                    style={{
                      padding: "8px 12px",
                      background: "#f5f5f5",
                      borderRadius: "6px",
                      minHeight: "80px",
                      whiteSpace: "pre-wrap",
                      color: "#333",
                      fontSize: "14px",
                      lineHeight: "1.6",
                    }}
                  >
                    {unionResponse || <span style={{ color: "#aaa" }}>No response yet.</span>}
                  </div>
                </Form.Item>
              )}
            </>
          )}

          {/* Buttons */}
          <Form.Item>
            {!isViewMode && (
              <Button type="primary" htmlType="submit" block>
                Submit Feedback
              </Button>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", flexDirection: "row", gap: "20px" }}>

              <Button
                style={{ marginTop: 10 }}
                block
                onClick={handleClear}
              >
                Clear
              </Button>

              {/* Back to Step Selection */}
              <Button
                style={{ marginTop: 10 }}
                block
                onClick={() => {
                  handleClear();
                  setStep(null);
                }}
              >
                Back
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div >
    </div >
  );
};

export default FeedbackPage;